import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Spin } from 'ALIASED-antd';
import { Alert } from 'reactstrap';
import styled from '@emotion/styled';
import { useAdobeClientId } from 'vl-common/src/hooks/useAdobeClientId';
import { useAdobeDCView } from 'vl-common/src/hooks/useAdobeDCView';
import { InfoModal } from 'vl-common/src/components/Modals';
import useApi from 'vl-common/src/hooks/useApi';
import formatDate from 'vl-common/src/lib/formatDate';
import { annotateCurrentLetterPdf } from 'vl-common/src/utils/pdfUtils/annotateCurrentLetter';

const DOCUMENT_PREVIEW_DIV_ID = 'document-preview-viewer';

const PreviewContainer = styled.div`
  min-height: 75vh;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 75vh;
`;

const ViewerContainer = styled.div`
  height: 75vh;
  width: 100%;
`;

interface DocumentPreviewModalProps {
  open: boolean;
  onClose: () => void;
  patientGuid?: string;
  fileGuid?: string;
  title?: string;
  uploadedBy?: string;
  createdDate?: string;
  annotateCurrentLetter?: boolean;
}

export const LetterFilePreviewModal = ({
  open,
  onClose,
  patientGuid,
  fileGuid,
  title,
  uploadedBy,
  createdDate,
  annotateCurrentLetter
}: DocumentPreviewModalProps) => {
  const { t } = useTranslation('common');
  const { Api } = useApi();
  const containerDiv = useRef<HTMLDivElement>(null);
  const clientId = useAdobeClientId();
  const { adobeDC, isLoading: isAdobeLoading, error: adobeError } = useAdobeDCView();

  const {
    data: previewUrl,
    isLoading: isUrlLoading,
    error: urlError
  } = useQuery({
    queryKey: ['documentPreviewUrl', patientGuid, fileGuid, annotateCurrentLetter],
    queryFn: async () => {
      const res = await Api.file.getPreSignedS3Url(patientGuid, { filename: fileGuid });
      const url = res?.data?.url;
      if (!url) {
        throw new Error(`Could not locate file: "${fileGuid}"`);
      }

      const pdfBytes = await fetch(url).then((response) => response.arrayBuffer());

      if (!annotateCurrentLetter) {
        return URL.createObjectURL(new Blob([pdfBytes], { type: 'application/pdf' }));
      }

      const editedByDate = createdDate ? formatDate(new Date(createdDate), "do MMMM yyyy 'at' HH:mm", 'en-GB') : '';
      const annotatedPdfBytes = await annotateCurrentLetterPdf(pdfBytes, uploadedBy || '', editedByDate);

      return URL.createObjectURL(new Blob([annotatedPdfBytes as BlobPart], { type: 'application/pdf' }));
    },
    enabled: open && !!patientGuid && !!fileGuid,
    staleTime: Infinity,
    gcTime: 0
  });

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  useEffect(() => {
    if (!open || !containerDiv.current || !adobeDC || !previewUrl) return;

    new adobeDC.View({ clientId, divId: DOCUMENT_PREVIEW_DIV_ID }).previewFile(
      {
        content: { location: { url: previewUrl } },
        metaData: { fileName: fileGuid }
      },
      {
        enableFormFilling: false,
        showDownloadPDF: false,
        showPrintPDF: false,
        showAnnotationTools: false,
        showBookmarks: false,
        showThumbnails: false
      }
    );
  }, [open, adobeDC, previewUrl, clientId, fileGuid]);

  const isLoading = isUrlLoading || isAdobeLoading;
  const error = urlError || adobeError;

  return (
    <InfoModal
      open={open}
      title={title ? `${t('Preview of')} ${title}` : t('Document Preview')}
      onCancel={onClose}
      onOk={onClose}
      footer={null}
      width="100%"
    >
      <PreviewContainer>
        {isLoading && (
          <LoadingContainer>
            <Spin size="large" tip={isAdobeLoading ? t('Loading viewer...') : t('Loading document...')} />
          </LoadingContainer>
        )}
        {!isLoading && error && (
          <Alert color="danger">{t('Unable to preview this document. Please try again later.')}</Alert>
        )}
        {!isLoading && !error && <ViewerContainer ref={containerDiv} id={DOCUMENT_PREVIEW_DIV_ID} />}
      </PreviewContainer>
    </InfoModal>
  );
};

export default LetterFilePreviewModal;
